import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Plus, Trash2, Save, RotateCcw } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { DAMM, FLODESSTATUS, LAYERS, SOURCE_TYPES } from "../lib/constants";

export const inputCls = "w-full bg-abyss/60 border border-border rounded-lg px-3 py-2 text-sm text-slate-100 outline-none focus:border-current/60 transition-colors";
export const labelCls = "font-mono text-[10px] uppercase tracking-widest text-slate-500 block mb-1.5";

export function Modal({ open, onClose, title, children, testid, wide }) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid={testid}>
            <div className="absolute inset-0 bg-abyss/70 backdrop-blur-sm" onClick={onClose} />
            <div className={`relative glass-panel rounded-2xl w-full ${wide ? "max-w-2xl" : "max-w-lg"} max-h-[85vh] overflow-y-auto panel-scroll p-6 shadow-2xl`}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display font-bold text-lg text-slate-100">{title}</h3>
                    <button data-testid={`${testid}-close`} onClick={onClose} className="text-slate-500 hover:text-slate-200 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
}

export function ProjectDialog({ open, onClose, onCreated }) {
    const [form, setForm] = useState({ name: "", organisation: "", description: "" });
    const [loading, setLoading] = useState(false);

    const submit = async () => {
        if (!form.name.trim()) return;
        setLoading(true);
        try {
            const r = await api.post("/projects", form);
            toast.success(`Projektet "${r.data.name}" skapat`);
            onCreated(r.data);
            onClose();
            setForm({ name: "", organisation: "", description: "" });
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose} title="Nytt projekt" testid="project-dialog">
            <div className="space-y-4">
                <div>
                    <label className={labelCls}>Projektnamn</label>
                    <input data-testid="project-name-input" className={inputCls} value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="T.ex. Nordviks kommun" />
                </div>
                <div>
                    <label className={labelCls}>Organisation</label>
                    <input data-testid="project-org-input" className={inputCls} value={form.organisation}
                        onChange={(e) => setForm({ ...form, organisation: e.target.value })} placeholder="Valfritt" />
                </div>
                <div>
                    <label className={labelCls}>Beskrivning</label>
                    <textarea data-testid="project-desc-input" className={inputCls} rows={3} value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Kort om uppdraget" />
                </div>
                <button data-testid="project-create-submit" onClick={submit} disabled={loading || !form.name.trim()}
                    className="w-full bg-current text-abyss font-semibold text-sm rounded-lg py-2.5 hover:bg-tide transition-colors disabled:opacity-50">
                    {loading ? "Skapar …" : "Skapa projekt"}
                </button>
            </div>
        </Modal>
    );
}

export function FlowDialog({ open, onClose, projectId, nodes, onSaved }) {
    const [form, setForm] = useState({ source: "", target: "", label: "", volume: 3, tempo: 3, flodesstatus: "nar_fram" });
    const [loading, setLoading] = useState(false);

    useEffect(() => { if (open) setForm({ source: "", target: "", label: "", volume: 3, tempo: 3, flodesstatus: "nar_fram" }); }, [open]);

    const submit = async () => {
        setLoading(true);
        try {
            await api.post(`/projects/${projectId}/flows`, { ...form, volume: Number(form.volume), tempo: Number(form.tempo) });
            toast.success("Flöde skapat");
            onSaved();
            onClose();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose} title="Nytt flöde" testid="flow-dialog">
            <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className={labelCls}>Från</label>
                        <select data-testid="flow-source-select" className={inputCls} value={form.source}
                            onChange={(e) => setForm({ ...form, source: e.target.value })}>
                            <option value="">Välj nod …</option>
                            {nodes.map((n) => <option key={n.id} value={n.id}>{n.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className={labelCls}>Till</label>
                        <select data-testid="flow-target-select" className={inputCls} value={form.target}
                            onChange={(e) => setForm({ ...form, target: e.target.value })}>
                            <option value="">Välj nod …</option>
                            {nodes.filter((n) => n.id !== form.source).map((n) => <option key={n.id} value={n.id}>{n.label}</option>)}
                        </select>
                    </div>
                </div>
                <div>
                    <label className={labelCls}>Beskrivning av flödet</label>
                    <input data-testid="flow-label-input" className={inputCls} value={form.label}
                        onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="T.ex. Ansökningar (papper + mejl)" />
                </div>
                <div>
                    <label className={labelCls}>Informationsstatus</label>
                    <select data-testid="flow-status-select" className={inputCls} value={form.flodesstatus}
                        onChange={(e) => setForm({ ...form, flodesstatus: e.target.value })}>
                        {Object.entries(FLODESSTATUS).map(([id, s]) => <option key={id} value={id}>{s.label}</option>)}
                    </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className={labelCls}>Volym ({form.volume}/5)</label>
                        <input data-testid="flow-volume-input" type="range" min="1" max="5" value={form.volume}
                            onChange={(e) => setForm({ ...form, volume: e.target.value })} className="w-full accent-sky-400" />
                    </div>
                    <div>
                        <label className={labelCls}>Tempo ({form.tempo}/5)</label>
                        <input data-testid="flow-tempo-input" type="range" min="1" max="5" value={form.tempo}
                            onChange={(e) => setForm({ ...form, tempo: e.target.value })} className="w-full accent-sky-400" />
                    </div>
                </div>
                <button data-testid="flow-create-submit" onClick={submit} disabled={loading || !form.source || !form.target}
                    className="w-full bg-current text-abyss font-semibold text-sm rounded-lg py-2.5 hover:bg-tide transition-colors disabled:opacity-50">
                    {loading ? "Skapar …" : "Skapa flöde"}
                </button>
            </div>
        </Modal>
    );
}

export function BottleneckDialog({ open, onClose, projectId, target, onSaved }) {
    const [form, setForm] = useState({ typ: "fordrojning", custom_typ: "", allvarlighet: 3, notering: "", status: "bekraftad" });
    const [loading, setLoading] = useState(false);

    useEffect(() => { if (open) setForm({ typ: "fordrojning", custom_typ: "", allvarlighet: 3, notering: "", status: "bekraftad" }); }, [open]);

    const submit = async () => {
        setLoading(true);
        try {
            await api.post(`/projects/${projectId}/bottlenecks`, {
                target_type: target.target_type, target_id: target.target_id,
                typ: form.typ, custom_typ: form.typ === "egen" ? form.custom_typ : null,
                allvarlighet: Number(form.allvarlighet), notering: form.notering, status: form.status,
            });
            toast.success("Damm registrerad");
            onSaved();
            onClose();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose} title={`Sätt damm – ${target?.label || ""}`} testid="bottleneck-dialog">
            <div className="space-y-4">
                <div>
                    <label className={labelCls}>Typ av flaskhals</label>
                    <div className="grid grid-cols-2 gap-2">
                        {Object.entries(DAMM).map(([id, d]) => (
                            <button key={id} data-testid={`damm-type-${id}`} onClick={() => setForm({ ...form, typ: id })}
                                className={`rounded-lg border px-3 py-2 text-sm font-medium text-left transition-all ${form.typ === id ? "border-current" : "border-border hover:border-slate-600"}`}
                                style={form.typ === id ? { borderColor: d.color, background: `${d.color}18`, color: d.color } : { color: "#94A3B8" }}>
                                {d.label}
                            </button>
                        ))}
                        <button data-testid="damm-type-egen" onClick={() => setForm({ ...form, typ: "egen" })}
                            className={`rounded-lg border px-3 py-2 text-sm font-medium text-left transition-all col-span-2 ${form.typ === "egen" ? "border-slate-400 bg-slate-400/10 text-slate-200" : "border-border text-slate-400 hover:border-slate-600"}`}>
                            Egen typ …
                        </button>
                    </div>
                    {form.typ === "egen" && (
                        <input data-testid="damm-custom-input" className={`${inputCls} mt-2`} value={form.custom_typ}
                            onChange={(e) => setForm({ ...form, custom_typ: e.target.value })} placeholder="Namnge din flaskhalstyp" />
                    )}
                </div>
                <div>
                    <label className={labelCls}>Allvarlighetsgrad ({form.allvarlighet}/5)</label>
                    <div className="flex gap-1.5">
                        {[1, 2, 3, 4, 5].map((v) => (
                            <button key={v} data-testid={`damm-severity-${v}`} onClick={() => setForm({ ...form, allvarlighet: v })}
                                className={`flex-1 rounded-lg border py-2 text-sm font-semibold transition-all ${form.allvarlighet >= v ? "bg-amber-500/20 border-amber-500/60 text-amber-400" : "border-border text-slate-500"}`}>
                                {v}
                            </button>
                        ))}
                    </div>
                </div>
                <div>
                    <label className={labelCls}>Notering</label>
                    <textarea data-testid="damm-note-input" className={inputCls} rows={3} value={form.notering}
                        onChange={(e) => setForm({ ...form, notering: e.target.value })} placeholder="Vad händer här? Vad säger empirin?" />
                </div>
                <div>
                    <label className={labelCls}>Status</label>
                    <select data-testid="damm-status-select" className={inputCls} value={form.status}
                        onChange={(e) => setForm({ ...form, status: e.target.value })}>
                        <option value="bekraftad">Bekräftad</option>
                        <option value="foreslagen">Förslag (ska granskas)</option>
                    </select>
                </div>
                <button data-testid="damm-save-button" onClick={submit} disabled={loading || (form.typ === "egen" && !form.custom_typ.trim())}
                    className="w-full bg-current text-abyss font-semibold text-sm rounded-lg py-2.5 hover:bg-tide transition-colors disabled:opacity-50">
                    {loading ? "Sparar …" : "Registrera damm"}
                </button>
            </div>
        </Modal>
    );
}

export function SourcesModal({ open, onClose, projectId, sources, refresh }) {
    const empty = { titel: "", typ: "intervju", datum: "", utdrag: "", konflikt: false, konflikt_med: "" };
    const [form, setForm] = useState(empty);
    const [loading, setLoading] = useState(false);

    const submit = async () => {
        setLoading(true);
        try {
            await api.post(`/projects/${projectId}/sources`, form);
            toast.success("Källa tillagd");
            setForm(empty);
            refresh();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    const remove = async (sid) => {
        await api.delete(`/sources/${sid}`);
        refresh();
    };

    return (
        <Modal open={open} onClose={onClose} title="Empiri & källor" testid="sources-modal" wide>
            <div className="space-y-3 mb-6">
                {sources.length === 0 && <p className="text-sm text-slate-500">Inga källor ännu. Lägg till intervjuer, observationer eller dokument.</p>}
                {sources.map((s) => (
                    <div key={s.id} data-testid={`source-row-${s.id}`} className="rounded-lg border border-border bg-abyss/40 p-3">
                        <div className="flex items-start justify-between gap-2">
                            <div>
                                <div className="text-sm font-semibold text-slate-200">{s.titel}</div>
                                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                                    {SOURCE_TYPES.find((t) => t.id === s.typ)?.label || s.typ} · {s.datum} · {s.lankar?.length || 0} länkar
                                </div>
                            </div>
                            <button data-testid={`source-delete-${s.id}`} onClick={() => remove(s.id)} className="text-slate-600 hover:text-red-400 transition-colors">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                        {s.utdrag && <p className="text-xs text-slate-400 mt-2 italic leading-relaxed">"{s.utdrag}"</p>}
                        {s.konflikt && (
                            <p className="text-[11px] text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1 mt-2">
                                Konflikterande vittnesmål{s.konflikt_med ? ` – mot "${s.konflikt_med}"` : ""}
                            </p>
                        )}
                    </div>
                ))}
            </div>
            <div className="border-t border-border pt-4 space-y-3">
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Ny källa (av-identifierad)</p>
                <input data-testid="source-title-input" className={inputCls} value={form.titel} placeholder="T.ex. Intervju 4 – enhetschef"
                    onChange={(e) => setForm({ ...form, titel: e.target.value })} />
                <div className="grid grid-cols-2 gap-3">
                    <select data-testid="source-type-select" className={inputCls} value={form.typ}
                        onChange={(e) => setForm({ ...form, typ: e.target.value })}>
                        {SOURCE_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                    </select>
                    <input data-testid="source-date-input" className={inputCls} value={form.datum} placeholder="T.ex. 2026-04"
                        onChange={(e) => setForm({ ...form, datum: e.target.value })} />
                </div>
                <textarea data-testid="source-excerpt-input" className={inputCls} rows={3} value={form.utdrag}
                    placeholder="Av-identifierat utdrag – inga namn eller personuppgifter"
                    onChange={(e) => setForm({ ...form, utdrag: e.target.value })} />
                <label className="flex items-center gap-2 text-sm text-slate-400">
                    <input data-testid="source-conflict-check" type="checkbox" checked={form.konflikt}
                        onChange={(e) => setForm({ ...form, konflikt: e.target.checked })} className="accent-amber-400" />
                    Konflikterande vittnesmål
                </label>
                {form.konflikt && (
                    <input data-testid="source-conflict-with" className={inputCls} value={form.konflikt_med} placeholder="Konflikterar mot vilken källa?"
                        onChange={(e) => setForm({ ...form, konflikt_med: e.target.value })} />
                )}
                <button data-testid="source-add-button" onClick={submit} disabled={loading || !form.titel.trim()}
                    className="w-full bg-current text-abyss font-semibold text-sm rounded-lg py-2.5 hover:bg-tide transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                    <Plus className="w-4 h-4" /> Lägg till källa
                </button>
            </div>
        </Modal>
    );
}

export function AuditModal({ open, onClose, projectId }) {
    const [rows, setRows] = useState([]);
    useEffect(() => {
        if (open && projectId) api.get(`/projects/${projectId}/audit`).then((r) => setRows(r.data)).catch(() => {});
    }, [open, projectId]);

    return (
        <Modal open={open} onClose={onClose} title="Audit-logg" testid="audit-modal" wide>
            <div className="space-y-1 font-mono text-[11px]">
                {rows.length === 0 && <p className="text-slate-500 text-sm font-sans">Inga händelser ännu.</p>}
                {rows.map((r) => (
                    <div key={r.id} className="flex gap-3 py-1.5 border-b border-border/50">
                        <span className="text-slate-600 shrink-0">{new Date(r.at).toLocaleString("sv-SE")}</span>
                        <span className="text-current shrink-0">{r.actor}</span>
                        <span className="text-slate-300">{r.action}</span>
                        {r.detail && <span className="text-slate-500 truncate">{r.detail}</span>}
                    </div>
                ))}
            </div>
        </Modal>
    );
}

export function VersionsModal({ open, onClose, projectId, onRestored }) {
    const [versions, setVersions] = useState([]);
    const [etikett, setEtikett] = useState("");

    const load = () => api.get(`/projects/${projectId}/versions`).then((r) => setVersions(r.data)).catch(() => {});
    useEffect(() => { if (open && projectId) load(); }, [open, projectId]);

    const save = async () => {
        await api.post(`/projects/${projectId}/versions`, { etikett });
        setEtikett("");
        toast.success("Version sparad");
        load();
    };

    const restore = async (vid) => {
        await api.post(`/versions/${vid}/restore`);
        toast.success("Version återställd");
        onRestored();
        onClose();
    };

    return (
        <Modal open={open} onClose={onClose} title="Versionshistorik" testid="versions-modal">
            <div className="flex gap-2 mb-4">
                <input data-testid="version-label-input" className={inputCls} value={etikett} placeholder="Etikett, t.ex. 'Före ledningsmöte'"
                    onChange={(e) => setEtikett(e.target.value)} />
                <button data-testid="version-save-button" onClick={save}
                    className="shrink-0 bg-current text-abyss font-semibold text-sm rounded-lg px-4 hover:bg-tide transition-colors flex items-center gap-1.5">
                    <Save className="w-4 h-4" /> Spara
                </button>
            </div>
            <div className="space-y-2">
                {versions.length === 0 && <p className="text-sm text-slate-500">Inga sparade versioner ännu.</p>}
                {versions.map((v) => (
                    <div key={v.id} data-testid={`version-row-${v.id}`} className="flex items-center justify-between rounded-lg border border-border bg-abyss/40 px-3 py-2.5">
                        <div>
                            <div className="text-sm font-medium text-slate-200">{v.etikett || "Utan etikett"}</div>
                            <div className="font-mono text-[10px] text-slate-500">{new Date(v.created_at).toLocaleString("sv-SE")}</div>
                        </div>
                        <button data-testid={`version-restore-${v.id}`} onClick={() => restore(v.id)}
                            className="text-xs text-current hover:text-tide flex items-center gap-1 transition-colors">
                            <RotateCcw className="w-3.5 h-3.5" /> Återställ
                        </button>
                    </div>
                ))}
            </div>
        </Modal>
    );
}
